import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Eye, Edit2, X, UserMinus, UserPlus, Archive, Download, Upload, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useFetch, Spinner } from '../lib/useFetch';
import { workersApi, companiesApi, workersImportApi, rolesApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { workerFields } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';
import { DismissWorkerDialog, RehireWorkerDialog } from '../components/DismissalDialogs';
import ImportWorkersModal from '../components/ImportWorkersModal';
import ReassignCompanyDialog from '../components/ReassignCompanyDialog';
import SortAZButton from '../components/SortAZButton';

const LavoratoriPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isClient = user?.role === 'client';
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [showDismessi, setShowDismessi] = useState(false);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [rehireTarget, setRehireTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    if (isClient) return;
    let cancelled = false;
    rolesApi.catalog().then((r) => { if (!cancelled) setRolesCatalog(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isClient]);

  const labelOfRole = (key) => {
    const e = rolesCatalog.find((x) => x.roleKey === key);
    return e ? e.roleLabel : key;
  };
  const ruoliOf = (w) => {
    const r = w?.ruoli;
    if (!r) return [];
    if (Array.isArray(r)) return r.filter(Boolean);
    return String(r).split(/[;,|/]+/).map((s) => s.trim()).filter(Boolean);
  };

  const statusParam = showDismessi ? 'all' : undefined;
  const { data: workers, loading, reload } = useFetch(() => workersApi.list({ status: statusParam || undefined }), [showDismessi]);
  const { data: companies } = useFetch(() => companiesApi.list({ status: 'all' }), []);
  const crud = useCrudActions({ api: workersApi, label: 'lavoratore', onChange: reload });

  const filtered = useMemo(() => {
    let list = workers || [];
    if (companyFilter !== 'all') list = list.filter((w) => w.companyId === companyFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((w) => `${w.firstName} ${w.lastName} ${w.fiscalCode} ${w.mansione}`.toLowerCase().includes(q));
    }
    // Ordinamento alfabetico sul NOME VISIBILE ("firstName lastName", come mostrato in tabella),
    // stile Excel: la stringa che l'utente vede è quella che viene ordinata.
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      const na = `${a.firstName || ''} ${a.lastName || ''}`.trim();
      const nb = `${b.firstName || ''} ${b.lastName || ''}`.trim();
      return na.localeCompare(nb, 'it', { sensitivity: 'base' }) * dir;
    });
  }, [search, companyFilter, workers, sortDir]);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || '—';

  const defaultsForNew = () => ({
    stato: 'attivo',
    companyId: isClient ? user.company : (companyFilter !== 'all' ? companyFilter : undefined),
  });

  if (loading) return <Spinner />;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="lavoratori-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-lg font-bold text-gray-800">
          Anagrafica lavoratori <span className="text-slate-500 font-normal text-sm">({filtered.length})</span>
          <span className="ml-2 text-[11px] font-normal text-slate-400">· ordinato per nome visibile ({sortDir === 'asc' ? 'A→Z' : 'Z→A'})</span>
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <SortAZButton
            direction={sortDir}
            onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            testId="lavoratori-sort-az"
            size="md"
          />
          <button
            onClick={() => setCompanyFilter(companyFilter === 'privati' ? 'all' : 'privati')}
            data-testid="lavoratori-toggle-privati"
            className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider ${companyFilter === 'privati' ? 'bg-[#023F80] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            title="Lavoratori privati (senza azienda o corsi personali)"
          >
            PRIVATI
          </button>
          {!isClient && (
            <>
              <button
                onClick={() => workersImportApi.exportXlsx({
                  companyId: companyFilter !== 'all' ? companyFilter : undefined,
                  status: showDismessi ? 'all' : 'attivo',
                })}
                data-testid="lavoratori-export-btn"
                className="px-4 py-2 rounded-full text-xs font-bold tracking-wider bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-2"
                title={companyFilter === 'privati' ? 'Esporta solo i lavoratori PRIVATI' : (companyFilter !== 'all' ? 'Esporta lavoratori azienda selezionata' : 'Esporta tutti i lavoratori visibili')}
              >
                <Download size={14} /> ESPORTA
              </button>
              <button
                onClick={() => setImportOpen(true)}
                data-testid="lavoratori-import-btn"
                className="ro-hide px-4 py-2 rounded-full text-xs font-bold tracking-wider bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-2"
              >
                <Upload size={14} /> IMPORTA
              </button>
            </>
          )}
          <button onClick={() => crud.onCreate(defaultsForNew())} className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2">
            <Plus size={14} /> AGGIUNGI LAVORATORE
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5 items-center">
        <div className="relative md:col-span-2">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, cognome, codice fiscale, mansione..." className="pl-9 h-10 border-gray-200" />
        </div>
        {!isClient && (
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="h-10 border-gray-200"><SelectValue placeholder="Tutte le aziende" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {(companies || []).filter(c => c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
          <Checkbox checked={showDismessi} onCheckedChange={(v) => setShowDismessi(!!v)} data-testid="lavoratori-toggle-dismessi" />
          <Archive size={13} className="text-slate-500" /> Mostra dismessi
        </label>
      </div>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Lavoratore</th>
              <th className="text-left bg-gray-100 px-4 py-3">Codice fiscale</th>
              <th className="text-left bg-gray-100 px-4 py-3">Data / Luogo nascita</th>
              <th className="text-left bg-gray-100 px-4 py-3">Azienda</th>
              <th className="text-left bg-gray-100 px-4 py-3">Mansione</th>
              <th className="text-left bg-gray-100 px-4 py-3">Ruoli</th>
              <th className="text-left bg-gray-100 px-4 py-3">Sede</th>
              <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
              <th className="bg-gray-100 rounded-r-xl px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((w) => {
              const isDismesso = w.status === 'dismesso';
              return (
                <tr key={w.id} className={`border-b border-gray-50 hover:bg-gray-50/60 cursor-pointer ${isDismesso ? 'opacity-70 bg-slate-50/50' : ''}`} data-testid={`lavoratore-row-${w.id}`} onClick={() => navigate(`/lavoratori/${encodeURIComponent(w.id)}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full ${isDismesso ? 'bg-slate-200 text-slate-500' : 'bg-[#e0e7ff] text-[#023F80]'} flex items-center justify-center font-bold text-xs`}>{(w.firstName||' ').charAt(0)}{(w.lastName||' ').charAt(0)}</div>
                      <div>
                        <div className="font-semibold text-gray-800">{w.firstName} {w.lastName}</div>
                        <div className="text-xs text-gray-500">{fmtDate(w.birthDate)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{w.fiscalCode || '—'}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs">
                    <div>{fmtDate(w.birthDate) || '—'}</div>
                    <div className="text-slate-500">{w.birthPlace || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs">{companyName(w.companyId)}</td>
                  <td className="px-4 py-3 text-gray-700">{w.mansione || '—'}</td>
                  <td className="px-4 py-3" data-testid={`lavoratore-ruoli-cell-${w.id}`}>
                    <div className="flex flex-wrap gap-1">
                      {ruoliOf(w).length === 0 && <span className="text-slate-400 text-xs">—</span>}
                      {ruoliOf(w).slice(0, 3).map((rk, i) => (
                        <span
                          key={`${rk}-${i}`}
                          title={rk}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-100"
                        >
                          {labelOfRole(rk)}
                        </span>
                      ))}
                      {ruoliOf(w).length > 3 && (
                        <span className="text-[10px] text-slate-500">+{ruoliOf(w).length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{w.sede || '—'}</td>
                  <td className="px-4 py-3">
                    {isDismesso ? (
                      <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold bg-slate-200 text-slate-700">Dismesso · {fmtDate(w.dismessoAt) || '—'}</span>
                    ) : (
                      <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold bg-[#dcfce7] text-[#15803d]">Attivo</span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => navigate(`/lavoratori/${encodeURIComponent(w.id)}`)} title="Apri" className="p-1.5 rounded-md hover:bg-gray-100"><Eye size={15} className="text-gray-600" /></button>
                      {!isClient && !isDismesso && <button onClick={() => crud.onEdit(w)} title="Modifica" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><Edit2 size={14} className="text-gray-600" /></button>}
                      {!isClient && !isDismesso && (
                        <button
                          onClick={() => setReassignTarget(w)}
                          title="Sposta in un'altra azienda"
                          data-testid={`lavoratore-reassign-${w.id}`}
                          className="ro-hide p-1.5 rounded-md hover:bg-indigo-50"
                        >
                          <ArrowRightLeft size={14} className="text-[#023F80]" />
                        </button>
                      )}
                      {!isClient && !isDismesso && (
                        <button onClick={() => setDismissTarget(w)} title="Dismetti" data-testid={`lavoratore-dismiss-${w.id}`} className="p-1.5 rounded-md hover:bg-red-50">
                          <UserMinus size={14} className="text-red-600" />
                        </button>
                      )}
                      {!isClient && isDismesso && (
                        <button onClick={() => setRehireTarget(w)} title="Riassumi" data-testid={`lavoratore-rehire-${w.id}`} className="px-2 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center gap-1">
                          <UserPlus size={12} /> RIASSUMI
                        </button>
                      )}
                      {!isClient && !isDismesso && <button onClick={() => crud.onDelete(w)} title="Elimina" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><X size={15} className="text-red-600" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-10 text-center text-sm text-gray-400">Nessun lavoratore trovato</div>}
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(v) => { if (!v) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica lavoratore' : 'Nuovo lavoratore'}
        description="Compila i dati anagrafici del lavoratore."
        fields={workerFields(companies || [], isClient, crud.editing?.companyId)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
        submitLabel={crud.editing?.id ? 'Aggiorna' : 'Crea lavoratore'}
      />

      <DismissWorkerDialog worker={dismissTarget} open={!!dismissTarget} onOpenChange={(v) => !v && setDismissTarget(null)} onDone={reload} />
      <RehireWorkerDialog worker={rehireTarget} open={!!rehireTarget} onOpenChange={(v) => !v && setRehireTarget(null)} onDone={reload} />
      <ReassignCompanyDialog
        open={!!reassignTarget}
        onOpenChange={(v) => !v && setReassignTarget(null)}
        worker={reassignTarget}
        currentCompanyName={reassignTarget ? companyName(reassignTarget.companyId) : ''}
        onDone={reload}
      />

      <ImportWorkersModal
        open={importOpen}
        onOpenChange={setImportOpen}
        companies={companies || []}
        selectedCompanyId={companyFilter}
        onDone={reload}
      />
    </div>
  );
};

export default LavoratoriPage;
